import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import analytics, { initCrossDomainTracking } from './utils/analytics';
import { useScrollDepth, addResourceHints, preloadImages, CRITICAL_IMAGES, measureWebVitals } from './utils/performance';
import { updatePageMeta, PAGE_META } from './utils/seo';

// Landing Page Component
const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Track scroll depth for engagement
  useScrollDepth((depth) => {
    analytics.trackScrollDepth(depth);
  });

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    analytics.trackEvent('scroll_navigation', 'Navigation', sectionId);
  };

  const handleCTAClick = (ctaName, location) => {
    analytics.trackCTA(ctaName, location);
  };

  const handleNavClick = (navItem) => {
    analytics.trackEvent('navigation_click', 'Header Navigation', navItem);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-indigo-900">
      {/* Header */}
      <header className="fixed top-0 w-full bg-slate-900/90 backdrop-blur-md z-50 border-b border-indigo-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">LL</span>
              </div>
              <span className="text-2xl font-bold text-white">LinguaLeap</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => { handleNavClick('Features'); scrollToSection('features'); }} className="text-gray-300 hover:text-indigo-400 transition-colors">Features</button>
              <button onClick={() => { handleNavClick('Benefits'); scrollToSection('benefits'); }} className="text-gray-300 hover:text-indigo-400 transition-colors">Benefits</button>
              <button onClick={() => { handleNavClick('Success Stories'); scrollToSection('testimonials'); }} className="text-gray-300 hover:text-indigo-400 transition-colors">Success Stories</button>
            </nav>
            <a href="https://www.lingua-leap.org" target="_blank" rel="noopener noreferrer"
               onClick={() => handleCTAClick('Download Now', 'Header')}
               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:scale-105">
              Download Now
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Transform from 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400"> Outsider </span>
                to 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400"> Insider</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Do you often feel like an outsider in conversations or gatherings? 
                <strong className="text-indigo-400"> Insiders get better pricing, more business deals, better chances of landing promotions, and discounted fees</strong> than outsiders.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://www.lingua-leap.org" target="_blank" rel="noopener noreferrer"
                   onClick={() => handleCTAClick('Start Your Transformation', 'Hero')}
                   className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:scale-105 text-center">
                  Start Your Transformation
                </a>
                <button onClick={() => { handleCTAClick('Learn More', 'Hero'); scrollToSection('features'); }}
                        className="border-2 border-indigo-500 text-indigo-400 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-500 hover:text-white transition-all">
                  Learn More
                </button>
              </div>
            </div>
            <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbW11bmljYXRpb258ZW58MHx8fHwxNzU4MTYxODE1fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional Communication"
                loading="eager"
                fetchpriority="high"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-800/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-8">The Hidden Barrier to Your Success</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-700/50 p-6 rounded-xl border border-red-500/20">
              <div className="text-red-400 text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-white mb-3">The Challenge</h3>
              <p className="text-gray-300">While English serves as a lingua franca, the nuances, professional jargon and unspoken cultural codes vary drastically.</p>
            </div>
            <div className="bg-slate-700/50 p-6 rounded-xl border border-orange-500/20">
              <div className="text-orange-400 text-4xl mb-4">📉</div>
              <h3 className="text-xl font-semibold text-white mb-3">The Cost</h3>
              <p className="text-gray-300">Misinterpretations and cultural faux pas lead to reduced productivity, team breakdowns, and missed opportunities.</p>
            </div>
            <div className="bg-slate-700/50 p-6 rounded-xl border border-green-500/20">
              <div className="text-green-400 text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-semibold text-white mb-3">The Solution</h3>
              <p className="text-gray-300">LinguaLeap develops your communication ability, making you fluent in any skill, industry, profession, or cultural context.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Master Every Professional Context</h2>
            <p className="text-xl text-gray-300">All the tools you need to become an insider</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <FeatureCard 
              icon="⚡"
              title="Word Power Sprints"
              description="Sharpen your vocabulary and grammar in fast-paced, engaging quizzes."
              image="https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbW11bmljYXRpb258ZW58MHx8fHwxNzU4MTYxODE1fDA&ixlib=rb-4.1.0&q=85"
            />
            <FeatureCard 
              icon="🎯"
              title="Pitch Mastery"
              description="Learn to articulate your vision and strategy with proven frameworks from top business thinkers."
              image="https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNvbW11bmljYXRpb258ZW58MHx8fHwxNzU4MTYxODE1fDA&ixlib=rb-4.1.0&q=85"
            />
            <FeatureCard 
              icon="🤖"
              title="AI Role-Play"
              description="Practice difficult conversations, from asking for a raise to giving critical feedback, in a safe environment."
              image="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzU4MTYxODI2fDA&ixlib=rb-4.1.0&q=85"
            />
            <FeatureCard
              icon="✍️"
              title="AI Writing Coach"
              description="Get instant, actionable feedback on your emails, reports, and other professional writing."
              image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw1fHxidXNpbmVzcyUyMHdyaXRpbmd8ZW58MHx8fHwxNzU4MTYxODE1fDA&ixlib=rb-4.1.0&q=85"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Before Your Next Big Moment</h2>
            <p className="text-xl text-gray-300">Sharpen your communication for only a few minutes. Be an insider.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <BenefitCard 
              icon="💼"
              title="Job Interviews"
              description="Master the subtle communication cues that make interviewers remember you positively."
            />
            <BenefitCard 
              icon="🤝"
              title="Business Meetings"
              description="Navigate complex negotiations and discussions with confidence and cultural awareness."
            />
            <BenefitCard 
              icon="📈"
              title="Performance Reviews"
              description="Articulate your value and achievements in ways that resonate with decision-makers."
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Success Stories</h2>
            <p className="text-xl text-gray-300">Join thousands who transformed from outsiders to insiders</p>
          </div>
          
          <div className="bg-slate-800/50 p-8 rounded-2xl border border-indigo-500/20">
            <div className="flex items-center mb-6">
              <img src="https://images.pexels.com/photos/2422280/pexels-photo-2422280.jpeg" 
                   alt="Success Story" 
                   className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h4 className="text-white font-semibold">Shabeeh A.</h4>
                <p className="text-gray-400">Senior Professional</p>
              </div>
            </div>
            <blockquote className="text-lg text-gray-300 italic leading-relaxed">
              "This is really good. I use it to sharpen myself whenever I have 1:1s with my Manager at work and meet with clients. I am able to sharpen my communication for each context."
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Become an Insider?</h2>
          <p className="text-xl text-indigo-100 mb-8">
            Transform your professional communication today. Join thousands who've made the leap from outsider to insider.
          </p>
          <a href="https://www.lingua-leap.org" target="_blank" rel="noopener noreferrer"
             onClick={() => handleCTAClick('Download LinguaLeap Now', 'CTA Section')}
             className="inline-block bg-white text-indigo-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all transform hover:scale-105">
            Download LinguaLeap Now
          </a>
          <p className="text-indigo-200 mt-4">www.lingua-leap.org</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">LL</span>
            </div>
            <span className="text-2xl font-bold text-white">LinguaLeap</span>
          </div>
          <div className="flex items-center justify-center space-x-6 mb-4">
            <a href="https://www.youtube.com/@VentureBuildingSeries" target="_blank" rel="noopener noreferrer"
               onClick={() => analytics.trackSocialClick('YouTube', 'Footer')}
               className="text-gray-400 hover:text-red-500 transition-colors flex items-center space-x-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="text-sm">Venture Building Series</span>
            </a>
          </div>
          <p className="text-gray-400">© 2025 VantagePoints, Inc. Transform from outsider to insider.</p>
        </div>
      </footer>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, image }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="bg-slate-800/50 rounded-2xl overflow-hidden border border-indigo-500/20 hover:border-indigo-500/50 transition-all duration-300 transform hover:scale-105"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-48 overflow-hidden bg-slate-700/30">
        <img
          src={image}
          alt={title}
          loading="eager"
          fetchpriority="high"
          className={`w-full h-full object-cover transition-transform duration-300 ${isHovered ? 'scale-110' : 'scale-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
        <div className="absolute bottom-4 left-4 text-4xl">{icon}</div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-300">{description}</p>
      </div>
    </div>
  );
};

// Benefit Card Component
const BenefitCard = ({ icon, title, description }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="bg-slate-800/50 p-6 rounded-xl border border-indigo-500/20 hover:border-indigo-500/50 transition-all duration-300 transform hover:scale-105"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`text-4xl mb-4 transition-transform duration-300 ${isHovered ? 'scale-110' : 'scale-100'}`}>{icon}</div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  );
};

// Social Media Posts Component
const SocialMediaPosts = () => {
  return (
    <div className="min-h-screen bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-12 text-center">Social Media Content</h1>
        
        {/* LinkedIn Posts */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-indigo-400 mb-8">LinkedIn Posts</h2>
          
          <div className="space-y-8">
            <LinkedInPost 
              content="🚀 Do you often feel like an outsider in conversations or gatherings?

Here's the truth: Insiders get better pricing, more business deals, better chances of landing promotions, and discounted fees than outsiders.

While English serves as a lingua franca, the nuances, professional jargon and unspoken cultural codes vary drastically across industries and cultures.

The cost? Misinterpretations and cultural faux pas lead to:
❌ Reduced productivity
❌ Team breakdowns  
❌ Missed opportunities

LinguaLeap develops your communication ability, making you fluent in any skill, industry, profession, or cultural context.

Transform from outsider to insider. Before your next job interview, business meeting or performance appraisal, sharpen your communication for only a few minutes.

Be an insider. Download LinguaLeap @ www.lingua-leap.org today.

#ProfessionalDevelopment #Communication #CareerGrowth #BusinessEnglish #Leadership"
              image="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzU4MTYxODI2fDA&ixlib=rb-4.1.0&q=85"
            />
            
            <LinkedInPost 
              content="💡 The difference between outsiders and insiders in professional settings isn't just about language fluency—it's about understanding the unspoken codes.

✅ Insiders know when to speak up in meetings
✅ Insiders understand cultural nuances in communication
✅ Insiders master professional jargon that opens doors
✅ Insiders navigate office politics with confidence

Before your next big moment—whether it's a job interview, client presentation, or team meeting—give yourself the insider advantage.

LinguaLeap transforms your professional communication in just minutes a day.

Ready to level up? www.lingua-leap.org

#CareerSuccess #ProfessionalCommunication #BusinessSkills #WorkplaceSuccess"
              image="https://images.pexels.com/photos/7821915/pexels-photo-7821915.jpeg"
            />
          </div>
        </div>

        {/* Instagram Banners */}
        <div>
          <h2 className="text-2xl font-bold text-indigo-400 mb-8">Instagram Banner Ads</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <InstagramBanner 
              headline="From Outsider to Insider"
              subtext="Master professional communication"
              cta="Download Now"
              image="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbW11bmljYXRpb258ZW58MHx8fHwxNzU4MTYxODE1fDA&ixlib=rb-4.1.0&q=85"
            />
            
            <InstagramBanner 
              headline="Unlock Better Deals"
              subtext="Insiders get promotions & opportunities"
              cta="Start Free"
              image="https://images.pexels.com/photos/2422280/pexels-photo-2422280.jpeg"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// LinkedIn Post Component
const LinkedInPost = ({ content, image }) => {
  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-indigo-500/20">
      <div className="flex items-center mb-4">
        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
          <span className="text-white font-bold">LL</span>
        </div>
        <div>
          <h3 className="text-white font-semibold">LinguaLeap</h3>
          <p className="text-gray-400 text-sm">Professional Communication App</p>
        </div>
      </div>
      <img src={image} alt="LinkedIn Post" className="w-full h-64 object-cover rounded-lg mb-4" />
      <p className="text-gray-300 whitespace-pre-line leading-relaxed">{content}</p>
    </div>
  );
};

// Instagram Banner Component
const InstagramBanner = ({ headline, subtext, cta, image }) => {
  return (
    <div className="relative h-96 rounded-lg overflow-hidden">
      <img src={image} alt="Instagram Banner" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
      <div className="absolute inset-0 flex flex-col justify-end p-6 text-center">
        <h3 className="text-2xl font-bold text-white mb-2">{headline}</h3>
        <p className="text-gray-200 mb-4">{subtext}</p>
        <button className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2 rounded-full font-semibold">
          {cta}
        </button>
        <p className="text-gray-300 text-sm mt-2">lingua-leap.org</p>
      </div>
    </div>
  );
};

// Static Marketing Content Component
const StaticMarketingContent = () => {
  const [copiedText, setCopiedText] = useState('');

  const copyToClipboard = (text, label, contentType) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
    // Track copy event
    analytics.trackEvent('copy_content', 'Marketing Content', `${contentType}: ${label}`);
  };

  const linkedinPosts = [
    {
      id: 'linkedin-1',
      title: 'LinkedIn Post #1: Problem-Solution Focus',
      content: `🚀 Do you often feel like an outsider in conversations or gatherings?

Here's the truth: Insiders get better pricing, more business deals, better chances of landing promotions, and discounted fees than outsiders.

While English serves as a lingua franca, the nuances, professional jargon and unspoken cultural codes vary drastically across industries and cultures.

The cost? Misinterpretations and cultural faux pas lead to:
❌ Reduced productivity
❌ Team breakdowns  
❌ Missed opportunities

LinguaLeap develops your communication ability, making you fluent in any skill, industry, profession, or cultural context.

Transform from outsider to insider. Before your next job interview, business meeting or performance appraisal, sharpen your communication for only a few minutes.

Be an insider. Download LinguaLeap @ www.lingua-leap.org today.

#ProfessionalDevelopment #Communication #CareerGrowth #BusinessEnglish #Leadership`
    },
    {
      id: 'linkedin-2', 
      title: 'LinkedIn Post #2: Insider Advantages Focus',
      content: `💡 The difference between outsiders and insiders in professional settings isn't just about language fluency—it's about understanding the unspoken codes.

✅ Insiders know when to speak up in meetings
✅ Insiders understand cultural nuances in communication
✅ Insiders master professional jargon that opens doors
✅ Insiders navigate office politics with confidence

Before your next big moment—whether it's a job interview, client presentation, or team meeting—give yourself the insider advantage.

LinguaLeap transforms your professional communication in just minutes a day.

Ready to level up? www.lingua-leap.org

#CareerSuccess #ProfessionalCommunication #BusinessSkills #WorkplaceSuccess`
    },
    {
      id: 'linkedin-3',
      title: 'LinkedIn Post #3: Quick Impact Focus', 
      content: `⏰ Before your next job interview, business meeting or social gathering appraisal...

Sharpen your communication for only a few minutes. Be an insider.

Here's what insiders understand that outsiders don't:
🎯 The right words open doors
🎯 Cultural codes matter more than grammar
🎯 Professional jargon creates instant credibility
🎯 Timing in conversations is everything

LinguaLeap makes you fluent in any skill, industry, profession, or cultural context.

Transform from outsider to insider today.

Download LinguaLeap @ www.lingua-leap.org

#QuickWins #ProfessionalGrowth #Communication #CareerAdvancement`
    },
    {
      id: 'linkedin-4',
      title: 'LinkedIn Post #4: Statistics & Impact',
      content: `📊 Research shows: Professionals who master communication nuances earn 23% more than their peers.

The difference? They've moved from outsider to insider.

While you focus on English fluency, insiders master:
• Professional jargon that commands respect
• Cultural codes that build instant rapport  
• Timing that turns conversations into opportunities
• Nuances that separate leaders from followers

Misinterpretations and cultural faux pas cost you promotions, deals, and connections.

LinguaLeap develops your communication ability for any context—before your next big moment.

Transform from outsider to insider: www.lingua-leap.org

#SalaryIncrease #ProfessionalDevelopment #CommunicationSkills #CareerSuccess`
    }
  ];

  const bannerAds = [
    {
      id: 'banner-1',
      title: 'LinkedIn Banner Ad #1: Transform Focus',
      headline: 'Transform from Outsider to Insider',
      subtext: 'Master professional communication in minutes',
      description: 'Perfect for LinkedIn sponsored content or display ads',
      cta: 'Download Now',
      url: 'www.lingua-leap.org',
      targetAudience: 'Professionals, managers, job seekers',
      specifications: '1200x628px (LinkedIn feed), 1080x1080px (square)'
    },
    {
      id: 'banner-2', 
      title: 'LinkedIn Banner Ad #2: Benefits Focus',
      headline: 'Unlock Better Deals & Promotions',
      subtext: 'Insiders get opportunities outsiders miss',
      description: 'Emphasizes tangible career benefits',
      cta: 'Start Free',
      url: 'www.lingua-leap.org', 
      targetAudience: 'Mid-level professionals, career climbers',
      specifications: '1200x628px (LinkedIn feed), 728x90px (banner)'
    },
    {
      id: 'banner-3',
      title: 'LinkedIn Banner Ad #3: Quick Solution',
      headline: 'Sharpen Communication in Minutes',
      subtext: 'Before your next big meeting or interview',
      description: 'Appeals to busy professionals needing quick solutions',
      cta: 'Try LinguaLeap',
      url: 'www.lingua-leap.org',
      targetAudience: 'Busy executives, interview candidates',
      specifications: '1200x628px (LinkedIn feed), 300x250px (sidebar)'
    },
    {
      id: 'banner-4',
      title: 'LinkedIn Banner Ad #4: Problem Awareness',
      headline: 'Stop Being an Outsider',  
      subtext: 'Master the unspoken codes of professional success',
      description: 'Direct problem identification for maximum impact',
      cta: 'Get Started',
      url: 'www.lingua-leap.org',
      targetAudience: 'Professionals feeling excluded or overlooked',
      specifications: '1200x628px (LinkedIn feed), 970x250px (header)'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">LinguaLeap Marketing Content</h1>
          <p className="text-xl text-gray-300">Copy-ready LinkedIn posts and banner ads</p>
        </div>

        {/* LinkedIn Posts Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-indigo-400 mb-8">📝 LinkedIn Posts</h2>
          <p className="text-gray-300 mb-8">Ready-to-copy LinkedIn posts. Click "Copy" to copy to clipboard.</p>
          
          <div className="grid gap-8">
            {linkedinPosts.map((post) => (
              <div key={post.id} className="bg-slate-800 rounded-lg p-6 border border-indigo-500/20">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                  <button
                    onClick={() => copyToClipboard(post.content, post.id, 'LinkedIn Post')}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    {copiedText === post.id ? '✅ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <div className="bg-slate-700 rounded p-4">
                  <pre className="text-gray-300 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                    {post.content}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Banner Ads Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-indigo-400 mb-8">🎨 LinkedIn Banner Ads</h2>
          <p className="text-gray-300 mb-8">Banner ad specifications and copy for LinkedIn advertising campaigns.</p>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {bannerAds.map((banner) => (
              <div key={banner.id} className="bg-slate-800 rounded-lg p-6 border border-indigo-500/20">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-white">{banner.title}</h3>
                  <button
                    onClick={() => copyToClipboard(`${banner.headline}\n${banner.subtext}\n${banner.cta}\n${banner.url}`, banner.id, 'Banner Ad')}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    {copiedText === banner.id ? '✅ Copied!' : '📋 Copy'}
                  </button>
                </div>
                
                {/* Banner Preview */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 mb-4 text-center">
                  <h4 className="text-2xl font-bold text-white mb-2">{banner.headline}</h4>
                  <p className="text-indigo-100 mb-4">{banner.subtext}</p>
                  <button className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-semibold">
                    {banner.cta}
                  </button>
                  <p className="text-indigo-200 text-sm mt-2">{banner.url}</p>
                </div>

                {/* Banner Details */}
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-400">Description:</span>
                    <span className="text-gray-300 ml-2">{banner.description}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Target Audience:</span>
                    <span className="text-gray-300 ml-2">{banner.targetAudience}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Specifications:</span>
                    <span className="text-gray-300 ml-2">{banner.specifications}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Copy Templates */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-indigo-400 mb-8">📄 Additional Copy Templates</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Email Subject Lines */}
            <div className="bg-slate-800 rounded-lg p-6 border border-indigo-500/20">
              <h3 className="text-xl font-semibold text-white mb-4">📧 Email Subject Lines</h3>
              <div className="space-y-2">
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300">"Your Transformation Starts Now"</p>
                </div>
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300">"Still Feeling Like an Outsider?"</p>
                </div>
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300">"Master Any Professional Context in Minutes"</p>
                </div>
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300">"Before Your Next Big Meeting..."</p>
                </div>
              </div>
            </div>

            {/* Twitter/X Posts */}
            <div className="bg-slate-800 rounded-lg p-6 border border-indigo-500/20">
              <h3 className="text-xl font-semibold text-white mb-4">🐦 Twitter/X Posts</h3>
              <div className="space-y-4">
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300 text-sm">Feeling like an outsider in professional settings? 🤔 Insiders get better deals, promotions & networks. LinguaLeap transforms your communication skills in minutes. From outsider → insider 🚀 lingua-leap.org</p>
                </div>
                <div className="bg-slate-700 rounded p-3">
                  <p className="text-gray-300 text-sm">The hidden cost of being an "outsider": ❌ Missed promotions ❌ Lower salaries ❌ Fewer opportunities. It's about mastering professional communication codes. LinguaLeap fixes this. lingua-leap.org</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center">
          <a href="/" className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold mr-4 transition-colors">
            ← Back to Landing Page
          </a>
          <a href="/social" className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
            View Interactive Social Content →
          </a>
        </div>
      </div>
    </div>
  );
};

// AppContent - Handles routing, analytics, and SEO
const AppContent = () => {
  const location = useLocation();

  // Track page views and update SEO on route change
  useEffect(() => {
    const path = location.pathname;
    let pageKey = 'home';

    if (path === '/social') pageKey = 'social';
    else if (path === '/static') pageKey = 'static';

    // Update SEO meta tags
    updatePageMeta(PAGE_META[pageKey]);

    // Track page view in analytics
    analytics.trackPageView(path, PAGE_META[pageKey].title);
  }, [location]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/social" element={<SocialMediaPosts />} />
      <Route path="/static" element={<StaticMarketingContent />} />
    </Routes>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Initialize performance optimizations
    addResourceHints();
    preloadImages(CRITICAL_IMAGES);

    // Track Web Vitals
    measureWebVitals((metric, value) => {
      analytics.trackEvent(metric, 'Web Vitals', '', Math.round(value));
    });

    // Track performance metrics
    analytics.trackPerformance();

    // Initialize cross-domain tracking after DOM is ready
    const timer = setTimeout(() => {
      initCrossDomainTracking();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;